/**
 * src/utils/pagePermissions.jsx
 *
 * Gabarits de permissions — QUELLES PAGES un utilisateur peut ouvrir.
 *
 * A ne pas confondre avec utils/permissions.js, qui repond a une autre
 * question : QUELS PROJETS (isAdmin, canSeeAllProjects, canEditProject...).
 * Les deux se composent, aucun ne remplace l'autre. Une page fermee par le
 * gabarit reste fermee meme pour un admin de projet, et un projet invisible
 * le reste meme si la page est ouverte.
 *
 * La liste vient de la base, jamais du front : public.current_user_pages()
 * (SECURITY DEFINER) rend le tableau de cles autorisees pour l'utilisateur
 * courant. Un seul appel par session.
 *
 * FERMETURE PAR DEFAUT — le principe qui gouverne tout ce fichier :
 *   - tant que la reponse n'est pas arrivee, `ready` est faux et AUCUNE
 *     decision n'est prise. L'appelant n'affiche rien ; il ne redirige pas,
 *     sinon un utilisateur legitime serait ejecte pendant le chargement.
 *   - a la moindre erreur, ou sur une reponse vide/nulle, la liste devient
 *     vide : aucune page. Ne pas savoir n'ouvre jamais une porte.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { PROJECT_PAGE_ORDER } from '../constants/pageKeys';

// L'ordre des pages projet vient desormais de la source unique
// (constants/pageKeys.js), partagee avec Routes.jsx et l'editeur de gabarits.
// Re-export pour ne casser aucun import existant.
export { PROJECT_PAGE_ORDER } from '../constants/pageKeys';

const PagePermissionsContext = createContext(null);

export const PagePermissionsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();

  // null = pas encore de reponse. [] = reponse connue, aucune page.
  const [pages, setPages] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (authLoading) return;

      if (!user) {
        if (!cancelled) { setPages([]); setLoading(false); }
        return;
      }

      setLoading(true);
      try {
        // PostgrestBuilder est un PromiseLike sans .catch() : on await dans un
        // try/catch, jamais .catch() sur le builder.
        const { data, error } = await supabase?.rpc('current_user_pages');
        if (error) throw error;
        if (!cancelled) setPages(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('[pagePermissions] current_user_pages failed:', err?.message || err);
        if (!cancelled) setPages([]);   // fermeture par defaut
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id, authLoading]);

  const ready = !authLoading && !loading && Array.isArray(pages);

  /**
   * Une cle absente ou vide designe une page sans gabarit (profil, redirections)
   * : toujours ouverte. Tout le reste exige une reponse arrivee ET la cle.
   */
  const canAccessPage = useCallback(
    (pageKey) => {
      if (!pageKey) return true;
      if (!Array.isArray(pages)) return false;
      return pages?.includes(pageKey);
    },
    [pages]
  );

  /** Premiere page projet accessible, dans l'ordre du menu. null si aucune. */
  const firstProjectPage = useCallback(
    () => PROJECT_PAGE_ORDER?.find((p) => canAccessPage(p?.key))?.segment || null,
    [canAccessPage]
  );

  const value = useMemo(
    () => ({ pages: pages || [], loading: !ready, ready, canAccessPage, firstProjectPage }),
    [pages, ready, canAccessPage, firstProjectPage]
  );

  return (
    <PagePermissionsContext.Provider value={value}>
      {children}
    </PagePermissionsContext.Provider>
  );
};

// Hors provider : on ne sait rien, donc on n'ouvre rien.
const FALLBACK = {
  pages: [],
  loading: true,
  ready: false,
  canAccessPage: () => false,
  firstProjectPage: () => null,
};

export const usePagePermissions = () => useContext(PagePermissionsContext) || FALLBACK;

export default PagePermissionsProvider;
